import React, { useCallback, useRef, useState } from 'react';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { FormViewer, FormViewerHandle } from '@wix/form-viewer/widget';
import {
  FormError,
  FormStatus as WixFormStatus,
  FormValues,
} from '@wix/form-viewer';
import { st, classes } from './FormSection.st.css';
import { FormSectionDatahooks } from './datahooks';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import pageSettingsParams from '../../pageSettingsParams';
import pageStylesParams from '../../pageStylesParams';
import { BorderStyle } from '../../../WixServices/types';

export interface FormSectionProps {
  formId?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({ formId }) => {
  const bi = useBi();
  const { i18n } = useTranslation();
  const { isMobile } = useEnvironment();
  const formViewerRef = useRef<FormViewerHandle>(null);
  const settings = useSettings();
  const styles = useStyles();

  // Get the border style setting and map to input style (box or line)
  const borderStyle = settings.get(pageSettingsParams.fieldsBorderStyle);
  const inputStyle =
    borderStyle === BorderStyle.UNDERLINE ? 'line' : ('box' as const);

  // Form state - managed internally since FormViewer handles its own submit
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<FormError[]>([]);

  const handleFormChange = useCallback((values: FormValues) => {
    setFormValues(values);
  }, []);

  const handleFormValidate = useCallback((errors: FormError[]) => {
    setFormErrors(errors);
  }, []);

  const handleStatusChange = useCallback((status: WixFormStatus) => {
    // FormViewer manages its own loading state for submit
    // Add any additional status handling here if needed
  }, []);

  const handleSubmitSuccess = useCallback(() => {
    // FormViewer shows its own thank you message
    // Reset form values for potential re-submission
    setFormValues({});
    setFormErrors([]);
  }, []);

  const handleSubmitFailure = useCallback((error: unknown) => {
    console.error('Form submission failed:', error);
  }, []);

  const displayForm = styles.get(pageStylesParams._formDisplay);
  if (!displayForm) {
    return null;
  }

  if (!formId) {
    return (
      <div
        className={st(classes.root, { isMobile, inputStyle })}
        data-hook={FormSectionDatahooks.EmptyState}
      >
        <div className={classes.emptyState}>
          {/* Empty state - form not yet created */}
        </div>
      </div>
    );
  }

  return (
    <div
      className={st(classes.root, { isMobile, inputStyle })}
      data-hook={FormSectionDatahooks.Main}
    >
      <FormViewer
        formId={formId}
        values={formValues}
        errors={formErrors}
        i18n={i18n}
        bi={bi}
        ref={formViewerRef}
        onChange={handleFormChange}
        onValidate={handleFormValidate}
        onStatusChange={handleStatusChange}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitFailure={handleSubmitFailure}
      />
    </div>
  );
};
